import ApiService from '@services/api.service';
import AuthService from '../../services/auth.service';
import { Notify } from 'quasar';
import Antenne from '@app/src/models/unused/Antenne';

// Normalisation utilitaire centralisée. Accepte IRI, id, objet, rncp...
const normalizeId = (ref) => {
  if (ref === null || ref === undefined) return null;
  if (typeof ref === 'number') return String(ref);
  if (typeof ref === 'string') return ref.replace(/^\/+/, '');
  if (typeof ref === 'object') {
    if (ref['@id']) return String(ref['@id']).replace(/^\/+/, '');
    if (ref.id !== undefined && ref.id !== null) return String(ref.id);
    if (ref.rncp_identifier) return String(ref.rncp_identifier);
    if (ref.rncp) return String(ref.rncp);
    if (ref.rncp_code) return String(ref.rncp_code);
  }
  return null;
};

export const antennediplome = {
  state: {
    diplomes: [],
    diplome: null,
    selectedDiplome: null,
    selectedDiplomes: [],
    documents: []
  },
  mutations: {
    setDiplomes(state, diplomes) {
      state.diplomes = diplomes
    },
    setDiplome(state, diplome) {
      state.diplome = diplome
    },
    setSelectedDiplome(state, d) {
      state.selectedDiplome = d
    },
    setSelectedDiplomes(state, list) {
      state.selectedDiplomes = list || []
    },
    setDocuments(state, docs) {
      state.documents = docs || []
    },

    pushSelectedDiplome(state, diplome) {
      state.selectedDiplomes = state.selectedDiplomes || []
      state.selectedDiplomes.push(diplome)
    },

    removeDiplomeFromAntenne(state, diplomeRef) {
      const id = normalizeId(diplomeRef)
      if (!id) return
      state.selectedDiplomes = (state.selectedDiplomes || []).filter(
        (item) =>
          normalizeId(
            item && (item.diplome || item['@id'] || item.id || item)
          ) !== id
      )
    }
  },
  actions: {
    async fetchDiplomeById(context, Diplome) {
      return new Promise((resolve, reject) => {
        ApiService.getRequest(`${Diplome}`)
          .then((response) => {
            context.commit("setDiplome", response.data)
            resolve()
          })
          .catch((error) => {
            console.error(error)
            Notify.create({
              message: "Une erreur s'est produite",
              color: "red",
            })
            reject(error)
          })
      })
    },
    async linkDiplomeToAntenne(context, { diplomeId, antenneId }) {
      const diplomeEndpoint = (() => {
        const n = normalizeId(diplomeId) || diplomeId
        return /^\d+$/.test(String(n)) ? `antenne_diplomes/${n}` : n
      })()

      const item = await ApiService.getRequest(diplomeEndpoint)
      const antennes = Array.isArray(item.data.antennes)
        ? item.data.antennes.map((a) =>
            String(a['@id'] || a).replace(/^\/+/, '')
          )
        : []
      const target = String(antenneId)
        .replace(/^\/+/, '')
        .replace(/^antennes\//, '')
      if (
        !antennes.some(
          (a) =>
            a.endsWith(target) || a === `antennes/${target}` || a === target
        )
      ) {
        const payload = {
          ...item.data,
          antennes: [
            ...antennes.map((a) => (a.startsWith('/') ? a : `/${a}`)),
            `/antennes/${target}`
          ]
        }
        const res = await ApiService.postRequest(diplomeEndpoint, payload)
        context.commit('pushSelectedDiplome', res.data)
        Notify.create({ message: "Diplôme lié à l'antenne", color: 'green' })
        return res.data
      }
      return item.data
    },

    /** Retire l'IRI/id d'un diplôme de l'entité Antenne (PATCH). */
    async removeDiplomeFromSelectedAntenne(context, { antenneId, diplomeId }) {
      try {
        const antenne = await ApiService.getRequest(`${antenneId}`)
        let diplomes = antenne.data.diplomes || []

        const target = normalizeId(diplomeId)
        diplomes = diplomes.filter((d) => normalizeId(d) !== target)

        const payload = { diplomes }

        if (
          antenne.data &&
          antenne.data.uuid &&
          typeof antenne.data.uuid !== 'string'
        ) {
          antenne.data.uuid = String(antenne.data.uuid)
        }

        let endpoint = antenneId
        if (typeof endpoint === 'string' && endpoint.startsWith('/antennes/')) {
          endpoint = endpoint.replace('/antennes/', 'antennes/')
        } else if (typeof endpoint === 'number') {
          endpoint = `antennes/${endpoint}`
        }

        const response = await ApiService.patchRequest(endpoint, payload)
        try {
          context.commit('removeDiplomeFromAntenne', diplomeId)
        } catch (e) {
          /* silent */
        }
        Notify.create({
          message: "Le diplôme a été retiré de l'antenne avec succès",
          color: 'green'
        })
        return response.data
      } catch (error) {
        console.error(
          'Error removing diplome from antenne (selected) ',
          error && error.response ? error.response.data : error
        )
        Notify.create({
          message: "Erreur lors de la suppression du diplôme de l'antenne",
          color: 'negative'
        })
        throw error
      }
    },

    fetchDiplomesByCfa(context, cfaId) {
      return ApiService.getRequest(`antenne_diplomes_cfa/${cfaId}`)
        .then((response) => {
          context.commit('setDiplomes', response.data['hydra:member'])
        })
        .catch((error) => {
          console.error(error)
          Notify.create({
            message:
              "Une erreur s'est produite lors de la récupération des données",
            color: 'red'
          })
        })
    },

    /** Charge les diplomes d'une antenne donnée et met à jour l'état local */
    async fetchAntenneDiplomes(context, antenneId) {
      try {
        // normalise l'endpoint attendu par ApiService : "antennes/123"
        let endpoint = antenneId
        if (typeof endpoint === 'string' && endpoint.startsWith('/antennes/'))
          endpoint = endpoint.replace('/antennes/', 'antennes/')
        else if (
          typeof endpoint === 'string' &&
          endpoint.startsWith('antennes/')
        )
          endpoint = endpoint
        else if (typeof endpoint === 'number') endpoint = `antennes/${endpoint}`
        else if (typeof endpoint === 'string') endpoint = `antennes/${endpoint}`

        const res = await ApiService.getRequest(endpoint)
        // res.data.diplomes est le tableau attendu par l'API (ou hydra:member selon l'endpoint)
        const diplomes = Array.isArray(res.data.diplomes)
          ? res.data.diplomes
          : res.data['hydra:member'] || []
        // Mettre à jour l'état local / sélectionné
        context.commit('setSelectedDiplomes', diplomes)
        return res.data
      } catch (err) {
        console.error(
          'fetchAntenneDiplomes error',
          err && err.response ? err.response.data : err
        )
        throw err
      }
    },

    fetchDiplomes(context) {
      return ApiService.getRequest('/liste_diplomes')
        .then((response) => {
          context.commit('setDiplomes', response.data['hydra:member'])
        })
        .catch((error) => {
          console.error(error)
        })
    },

    async fetchSelectedDiplomeById(context, diplomeId) {
      try {
        const response = await ApiService.getRequest(
          `antenne_diplomes/${diplomeId}`
        )
        context.commit('setSelectedDiplome', response.data)
        return response.data
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        throw error
      }
    },

    async addDocumentDiplome(context, formData) {
      try {
        const response = await ApiService.postRequest(
          'antenne_diplome_document_administratifs',
          formData
        )
        context.commit('setDocuments', response.data)
        Notify.create({
          message: 'Le document a été ajouté avec succès',
          color: 'green'
        })
      } catch (error) {
        console.log(error)
        Notify.create({
          message: "Une erreur s'est produite lors de l'ajout du document",
          color: 'red'
        })
      }
    },

    // Création d'un diplôme et liaison automatique à une antenne
    async createDiplome(context, options) {
      let payload = null
      let antenneUuid = null

      if (options && (options.payload || options.antenneUuid)) {
        payload = options.payload || {}
        antenneUuid = options.antenneUuid || null
      } else {
        payload = options || {}
      }

      try {
        const body = { ...payload }
        if (antenneUuid) {
          body.antennes = [antenneUuid]
        }

        const response = await ApiService.postRequest('antenne_diplomes', body)

        context.commit('pushSelectedDiplome', response.data)
        Notify.create({ message: 'Diplôme créé et lié', color: 'green' })
        return response.data
      } catch (error) {
        const resp = error && error.response ? error.response : null
        console.error(
          'createDiplome error',
          error,
          resp && resp.data ? resp.data : resp
        )
        const msg =
          (resp &&
            resp.data &&
            (resp.data.message || resp.data['hydra:description'])) ||
          (error && error.message) ||
          'Erreur'
        Notify.create({
          message: `Erreur création diplôme: ${msg}`,
          color: 'negative'
        })
        throw error
      }
    },

    /** Supprime la relation diplome <-> antenne en privilégiant l'item propriétaire. */
    async removeDiplomeFromAntenne(context, { antenneUuid, diplomeUuid }) {
      try {
        const antenne = await ApiService.getRequest(`${antenneUuid}`)
        let diplomes = antenne.data.diplomes || []

        // Convert to IRIs
        const diplomesIri = diplomes.map((d) => {
          if (d === null || d === undefined) return d
          if (typeof d === 'object' && d['@id']) return String(d['@id'])
          return d
        })

        try {
          // On va chercher le diplome pour avoir sa liste d'antennes actuelle
          const diplomeItem = await ApiService.getRequest(diplomeUuid)
          let itemAntennes = Array.isArray(diplomeItem.data.antennes)
            ? diplomeItem.data.antennes.slice()
            : []

          // retirer l'antenne ciblée
          const filtered = itemAntennes.filter(
            (a) => (a['@id'] || a) !== antenneUuid
          )
          // payloadItem permet de mettre à jour la liste des antennes du diplôme
          const payloadItem = { ...diplomeItem.data, antennes: filtered }
          await ApiService.postRequest(diplomeUuid, payloadItem)

          const refAfter = await ApiService.getRequest(antenneUuid)
          try {
            context.commit('removeDiplomeFromAntenne', diplomeUuid)
          } catch (e) {
            /* silent */
          }
          Notify.create({
            message: 'Le diplôme a été retiré (via antenne_diplomes)',
            color: 'green'
          })
          return refAfter.data
        } catch (e) {
          console.debug(
            'Owning-side update failed, falling back to antenne PATCH',
            e && e.response ? e.response.data : e.message || e
          )
        }
      } catch (error) {
        Notify.create({
          message:
            'Erreur lors de la suppression du diplôme du dispositif de formation',
          color: 'negative'
        })
      }
    }
  },
  getters: {
    getDiplomes(state) {
      const store = require('@store/index').default
      const cfaHasInternalTraining =
        store.state?.auth?.user?.antenne?.cfa.formation_interne
      const cfaAcceptOnlyApprentices =
        store.state?.auth?.user?.antenne?.cfa.accept_apprentis_only
      const userIsSupervisor = AuthService.checkRole('ROLE_SUPERVISOR')
      if (userIsSupervisor && !cfaHasInternalTraining)
        return store.state?.auth?.user?.antenne.diplomes
      else if (
        userIsSupervisor &&
        cfaHasInternalTraining &&
        cfaAcceptOnlyApprentices
      )
        return store.state?.auth?.user?.antenne?.cfa.diplomes
      else if (
        !userIsSupervisor &&
        cfaHasInternalTraining &&
        cfaAcceptOnlyApprentices
      )
        return store.state?.auth?.user?.antenne.diplomes
      else {
        return store.state?.auth?.user?.antenne.diplomes
          ? store.state?.auth?.user?.antenne.diplomes
          : state.diplomes.filter((diplome) => {
              return diplome.antennes.includes(
                store.state?.auth?.user?.antenne['@id']
              )
            })
      }
    },
    getDiplome(state) {
      return state.diplome
    },
    getSelectedDiplome(state) {
      return state.selectedDiplome
    },
    getSelectedDiplomes(state) {
      return state.selectedDiplomes
    },
    getDocumentsDiplomes(state) {
      return state.documents
    }
  }
}



